import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Iconify from "@/components/iconify/iconify";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMySchools } from "@/hooks/useMySchools";
import { useFetchSections } from "@/hooks/useFetchSections";
import { useStartQaProvisionJob } from "@/hooks/useStartQaProvisionJob";
import { useQaProvisionJob } from "@/hooks/useQaProvisionJob";
import { QA_JOB_FINISHED } from "@/types/qaProvision";
import { ManifestInput, countManifestActionLines } from "./ManifestInput";
import { QaProvisionJobStatus } from "./QaProvisionJobStatus";

/** Kept in step with MAX_USERS / MAX_SECTIONS in the provisioning lambdas. */
const MAX_CLIMBERS = 10;
const MAX_SECTIONS = 5;

/** The number input is free-form, so keep it inside what the server accepts. */
const clampCount = (value: string): number =>
  Math.max(1, Math.min(MAX_CLIMBERS, Number(value) || 1));

interface Section {
  id: string;
  sectionName?: string;
}

/**
 * The backend for this page is only deployed to dev and staging, so on prod the
 * mutation isn't in the merged schema at all. Recognise that shape of failure
 * and explain it rather than showing a raw GraphQL validation error.
 */
const isMissingBackend = (error: Error | null): boolean =>
  !!error &&
  /FieldUndefined|Validation error|Cannot query field|does not exist/i.test(
    error.message,
  );

export const QaProvisionUserPage = () => {
  const currentUser = useCurrentUser();
  const { schools, isLoading: schoolsLoading } = useMySchools();

  const [schoolId, setSchoolId] = useState("");
  const [sectionIds, setSectionIds] = useState<string[]>([]);
  const [manifestText, setManifestText] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [count, setCount] = useState(1);
  const [jobId, setJobId] = useState<string | null>(null);

  const { data: sections = [] } = useFetchSections(schoolId) as {
    data?: Section[];
  };
  const { startJob, isStarting, error: startError } = useStartQaProvisionJob();
  const { job, error: pollError, isAbandoned } = useQaProvisionJob(jobId);

  // The raw role, not effectiveRole: effectiveRole also covers a superadmin with
  // an active district, but the server requires a `dad-` id, so such a user would
  // fill in the whole form only to be refused.
  const isDistrictAdmin = currentUser?.role === "districtadmin";
  // `!pollError` matters: a job we can't read is indistinguishable from one that
  // hasn't loaded yet, and without it a failed poll would leave the form disabled
  // with no way back short of a reload. `!isAbandoned` does the same for a job
  // that stopped reporting and will never reach a terminal status.
  const running =
    !!jobId &&
    !pollError &&
    !isAbandoned &&
    (!job || !QA_JOB_FINISHED.includes(job.status));

  // A section list only makes sense for the school it came from.
  useEffect(() => {
    setSectionIds([]);
  }, [schoolId]);

  const actionLines = useMemo(
    () => countManifestActionLines(manifestText),
    [manifestText],
  );

  const selectableSections = sections.slice(0, MAX_SECTIONS);

  const handleSectionChange = (value: string | string[]) => {
    const selected = typeof value === "string" ? value.split(",") : value;
    if (selected.includes("all")) {
      setSectionIds(
        sectionIds.length === selectableSections.length
          ? []
          : selectableSections.map((section) => section.id),
      );
      return;
    }
    // The server caps this too; stopping here keeps the refusal out of the way.
    setSectionIds(selected.slice(0, MAX_SECTIONS));
  };

  const handleProvision = async () => {
    setJobId(null);
    try {
      const newJobId = await startJob({
        sectionIds,
        manifestText,
        count,
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
        ...(password.trim() ? { password: password.trim() } : {}),
      });
      setJobId(newJobId);
    } catch {
      // Already surfaced through the mutation's error state below.
    }
  };

  if (!isDistrictAdmin) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Provision QA user
        </Typography>
        <Alert severity="error">This page is for district admins only.</Alert>
      </Container>
    );
  }

  const canProvision =
    !!sectionIds.length && actionLines > 0 && !isStarting && !running;

  const schoolName = schools.find(
    (school) => school.id === schoolId,
  )?.schoolName;
  const selectedSectionNames = sections
    .filter((section) => sectionIds.includes(section.id))
    .map((section) => section.sectionName)
    .filter(Boolean);

  return (
    <Container sx={{ py: 4 }} maxWidth="md">
      <Typography variant="h4" gutterBottom>
        Provision QA user
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Creates climber accounts in your district and applies a qa-climber
        manifest to each, then hands back their logins. Dev and staging only.
      </Typography>

      {job && <QaProvisionJobStatus job={job} />}

      {running ? (
        // Nothing on the form can be changed mid-run, and the progress above is
        // what the user is actually watching — so give it the page.
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Provisioning {count} climber{count === 1 ? "" : "s"}
            {schoolName ? ` in ${schoolName}` : ""}
            {selectedSectionNames.length
              ? ` · ${selectedSectionNames.join(", ")}`
              : ""}
            {` · ${actionLines} manifest line${actionLines === 1 ? "" : "s"}`}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl fullWidth disabled={schoolsLoading || isStarting}>
              <InputLabel id="qa-provision-school">School</InputLabel>
              <Select
                labelId="qa-provision-school"
                label="School"
                value={schoolId}
                onChange={(event) => setSchoolId(event.target.value)}
              >
                {schools.map((school) => (
                  <MenuItem key={school.id} value={school.id}>
                    {school.schoolName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!schoolId || isStarting}>
              <InputLabel id="qa-provision-sections">Classes</InputLabel>
              <Select
                labelId="qa-provision-sections"
                label="Classes"
                multiple
                value={sectionIds}
                onChange={(event) => handleSectionChange(event.target.value)}
                renderValue={(selected) =>
                  sections
                    .filter((section) => selected.includes(section.id))
                    .map((section) => section.sectionName)
                    .join(", ")
                }
              >
                <MenuItem value="all">
                  <Checkbox
                    checked={
                      selectableSections.length > 0 &&
                      sectionIds.length === selectableSections.length
                    }
                  />
                  <ListItemText
                    primary={
                      sections.length > MAX_SECTIONS
                        ? `Select first ${MAX_SECTIONS}`
                        : "Select all"
                    }
                  />
                </MenuItem>
                {sections.map((section) => (
                  <MenuItem
                    key={section.id}
                    value={section.id}
                    disabled={
                      sectionIds.length >= MAX_SECTIONS &&
                      !sectionIds.includes(section.id)
                    }
                  >
                    <Checkbox checked={sectionIds.includes(section.id)} />
                    <ListItemText primary={section.sectionName} />
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Up to {MAX_SECTIONS}, all in the school above
              </FormHelperText>
            </FormControl>
          </Stack>

          <ManifestInput
            value={manifestText}
            onChange={setManifestText}
            disabled={isStarting}
          />

          <Divider />

          <Typography variant="subtitle2">Optional</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="First name"
              fullWidth
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              disabled={isStarting}
              helperText="Default: QA"
            />
            <TextField
              label="Last name"
              fullWidth
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              disabled={isStarting}
              helperText="Default: Climber-<random>"
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Password"
              fullWidth
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={isStarting}
              helperText="Default: mspasswordv8. At least 8 characters."
            />
            <TextField
              label="How many climbers"
              type="number"
              fullWidth
              value={count}
              onChange={(event) => setCount(clampCount(event.target.value))}
              disabled={isStarting}
              inputProps={{ min: 1, max: MAX_CLIMBERS }}
              helperText={`1–${MAX_CLIMBERS}, all from the same manifest`}
            />
          </Stack>

          <Box>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleProvision}
              disabled={!canProvision}
              startIcon={
                isStarting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Iconify icon="mdi:account-plus" />
                )
              }
            >
              {isStarting
                ? "Starting…"
                : `Provision ${count} climber${count === 1 ? "" : "s"}`}
            </Button>
          </Box>
        </Stack>
      )}

      {startError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {isMissingBackend(startError)
            ? "The QA provisioning backend is only deployed on dev and staging."
            : startError.message}
        </Alert>
      )}
      {pollError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {pollError.message}
        </Alert>
      )}
      {isAbandoned && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          This job stopped reporting, so it will never finish — the worker most
          likely ran out of time. Any climbers it already created are listed
          above; check CloudWatch for the rest.
        </Alert>
      )}
    </Container>
  );
};
